import React, { useMemo } from 'react';
import Modal from '@components/ui/general/Modal/Modal';
import LoadoutSelector from './LoadoutSelector';
import { MapPin, Lock, Check } from 'lucide-react';
import { useT } from '@/i18n';
import './BotConfigModal.css';

/**
 * SpawnPreviewGrid - Visual representation of spawn positions
 * Shows a 6x6 grid with unit positions highlighted
 */
function SpawnPreviewGrid({ mode }) {
  // Define which cells are "active" (have units) for each mode
  // Grid is 6x6, positions are [row, col] from top-left
  const scatteredPositions = [
    [0, 4], [1, 1], [2, 5], [3, 0], [3, 3], [4, 2], [5, 4]
  ];
  const groupedPositions = [
    [0, 0], [0, 1], [1, 0], [1, 1], [2, 0], [2, 1], [0, 2]
  ];

  const activePositions = mode === 'scattered' ? scatteredPositions : groupedPositions;
  const positionSet = new Set(activePositions.map(([r, c]) => `${r}-${c}`));

  return (
    <div className="spawn-preview-grid">
      {Array.from({ length: 36 }).map((_, idx) => {
        const row = Math.floor(idx / 6);
        const col = idx % 6;
        const isActive = positionSet.has(`${row}-${col}`);
        return (
          <div
            key={idx}
            className={`spawn-preview-cell ${isActive ? 'spawn-preview-cell--active' : ''}`}
          />
        );
      })}
    </div>
  );
}

/**
 * Modal for configuring bot settings in lobby
 * Only the game creator can edit settings
 * Uses LoadoutSelector component for unit template selection
 * Uses instant-apply pattern (changes save immediately)
 */
export default function BotConfigModal({
  isOpen,
  onClose,
  currentConfig,
  loadoutTemplates = [],
  botRoster = [],
  onSave,
  onRegenerate,
  isSaving = false,
  isRegenerating = false,
  isLoadingTemplates = false,
  isCreator = false,
}) {
  const t = useT();

  // Whether the user can edit the bot config
  const canEdit = isCreator;

  // Current values from config
  const spawnMode = currentConfig?.spawn_mode || 'grouped';
  const selectedSlug = currentConfig?.unit_combo || 'random';

  // Map slug to template id (for LoadoutSelector)
  const appliedLoadoutId = useMemo(() => {
    const template = loadoutTemplates.find(t => t.slug === selectedSlug);
    return template?.id || null;
  }, [loadoutTemplates, selectedSlug]);

  // Determine active category from selected template
  const activeCategory = useMemo(() => {
    const template = loadoutTemplates.find(t => t.slug === selectedSlug);
    return template?.category || 'random';
  }, [loadoutTemplates, selectedSlug]);

  // Handle spawn mode change - instant apply
  const handleSpawnModeChange = (newSpawnMode) => {
    if (!canEdit) return;
    onSave({ spawn_mode: newSpawnMode, unit_combo: selectedSlug });
  };

  // Handle loadout selection - instant apply
  // FAIL LOUDLY: Template must exist and have slug
  const handleApplyLoadout = (templateId) => {
    if (!canEdit) return;
    const template = loadoutTemplates.find(t => t.id === templateId);
    if (!template) {
      throw new Error(`[BotConfigModal] Template not found for id: ${templateId}`);
    }
    if (!template.slug) {
      throw new Error(`[BotConfigModal] Template missing slug: ${JSON.stringify(template)}`);
    }
    onSave({ spawn_mode: spawnMode, unit_combo: template.slug });
  };

  // Handle regenerate random
  const handleRegenerateRandom = () => {
    if (!canEdit) return;
    onRegenerate?.({ spawn_mode: spawnMode, unit_combo: selectedSlug });
  };

  const spawnModeOptions = [
    {
      value: 'scattered',
      labelKey: 'bot_config.scattered',
      descKey: 'bot_config.scattered_desc',
    },
    {
      value: 'grouped',
      labelKey: 'bot_config.grouped',
      descKey: 'bot_config.grouped_desc',
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('bot_config.title')}
      className="bot-config-modal"
    >
      <div className="bot-config-content">
        {/* Read-only banner when not creator */}
        {!canEdit && (
          <div className="bot-config-readonly-banner">
            <Lock size={16} />
            <span>{t('lobby.only_creator_can_edit')}</span>
          </div>
        )}

        {/* Spawn Mode Section */}
        <div className="bot-config-section">
          <h4 className="bot-config-section-title">
            <MapPin size={18} />
            {t('lobby.spawn_position')}
          </h4>
          <div className="bot-config-options bot-config-options--grid">
            {spawnModeOptions.map((option) => {
              const isSelected = spawnMode === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`bot-config-option ${isSelected ? 'bot-config-option--selected' : ''} ${!canEdit ? 'bot-config-option--disabled' : ''} ${isSaving ? 'bot-config-option--saving' : ''}`}
                  onClick={() => handleSpawnModeChange(option.value)}
                  disabled={!canEdit || isSaving}
                >
                  <SpawnPreviewGrid mode={option.value} />
                  <div className="bot-config-option-content">
                    <span className="bot-config-option-label">{t(option.labelKey)}</span>
                    <span className="bot-config-option-description">{t(option.descKey)}</span>
                  </div>
                  {isSelected && (
                    <div className="bot-config-option-check">
                      <Check size={16} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Unit Combo Section - Using LoadoutSelector */}
        <div className="bot-config-section">
          <h4 className="bot-config-section-title">
            {t('lobby.unit_combo')}
          </h4>
          <LoadoutSelector
            loadoutTemplates={loadoutTemplates}
            activeCategory={activeCategory}
            appliedLoadoutId={appliedLoadoutId}
            purchasedUnits={botRoster.map(unit => {
              // FAIL LOUDLY: All units MUST have name
              if (!unit.name) {
                throw new Error(`[BotConfigModal] Bot roster unit missing name: ${JSON.stringify(unit)}`);
              }
              return {
                unit: {
                  name: unit.name,
                  unit_template: {
                    name: unit.name.split(' #')[0], // Remove "#123" suffix
                    visual_data: unit.visual_data,
                  },
                },
              };
            })}
            onApplyLoadout={handleApplyLoadout}
            onRegenerateRandom={handleRegenerateRandom}
            isApplying={isSaving || isRegenerating}
            isLoading={isLoadingTemplates}
            disabled={!canEdit}
            mode="bot"
          />
        </div>

      </div>
    </Modal>
  );
}
